/* Mega Office Supplies — quote-only cards
 *
 * Products on the "quote-only-products" theme template have no Add to Cart on their own
 * product page, by design. But the Searchspring search/collection cards, the predictive
 * search dropdown and the recommendations widget all still render "Add To Cart" for them,
 * so a customer can add a quote-only item to the cart without seeing the product page.
 *
 * Those cards come from assets/search-app.js (180KB) and product-recommendation-app.js
 * (1.27MB), both far over the themeFilesUpsert limit and therefore not editable. So the
 * button is rewritten after render instead of never being rendered.
 *
 * Identifying a quote-only card
 *   Shopify exposes template_suffix neither to the storefront nor to the Searchspring index,
 *   so there is no flag on the card. A list of quote-only handles is published to Shopify
 *   Files and fetched once per session; the card's own product link supplies the handle.
 *   Long-term fix: have template_suffix added to the Searchspring index and delete the list.
 *
 * Card geometry — the thing that broke the first version
 *   The Searchspring grid card root IS the product link:
 *     <a class="…" href="/products/{handle}"> …badge, img, ADD TO CART button…, vendor/title/price </a>
 *   Element.querySelector only searches DESCENDANTS, so testing an ancestor for "contains a
 *   product link" never matches that anchor. The walk then ran past the card to the results
 *   grid, which contains every card, and took the first handle in it — converting every card
 *   on the page to the verdict of the first product. Hence:
 *     - an ancestor is accepted if it IS a product link, or contains exactly ONE distinct one
 *     - more than one distinct handle in scope means we have overshot: refuse and leave alone
 *   Because the button sits inside an <a>, the replacement must not be another <a> (nested
 *   anchors are invalid and browsers unnest them). A <span> is used there; the click bubbles
 *   to the card anchor, which already points at the product page.
 *
 * Scope
 *   Presentation only. /cart/add.js still accepts these products for anyone calling it
 *   directly. Real enforcement needs inventory tracking at zero with "continue selling" off,
 *   or a Shopify checkout-validation function.
 *
 * Fails open: if the list cannot be loaded, nothing on the page is touched.
 * Debug: add ?quoteonly=debug to any URL.
 */
(function () {
  'use strict';

  if (window.__mosQuoteOnly) return;
  var API = (window.__mosQuoteOnly = {
    version: 3, handles: 0, matched: 0, converted: 0, refused: 0
  });

  var CFG = window.mosQuoteOnlyConfig || {};
  var LIST_URL = CFG.listUrl || '/cdn/shop/files/mos-quote-only.json';
  var LABEL = CFG.label || 'Add to Quote';
  var CACHE_KEY = 'mosQuoteOnly:v2';
  var MARK = 'data-mos-quote-only';
  var DONE = 'data-mos-quote-converted';
  var MAX_DEPTH = 12;
  var DEBUG = location.search.indexOf('quoteonly=debug') !== -1;

  var handles = null;

  /* ---------- the list ---------- */

  function loadList() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) {
          handles = new Set(arr);
          API.handles = handles.size;
          return Promise.resolve();
        }
      }
    } catch (e) {}

    return fetch(LIST_URL, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (doc) {
        var arr = (doc && doc.handles) || [];
        if (!Array.isArray(arr) || !arr.length) throw new Error('empty list');
        arr = arr.map(function (h) { return String(h).toLowerCase(); });
        handles = new Set(arr);
        API.handles = handles.size;
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(arr)); } catch (e) {}
      })
      .catch(function (err) {
        handles = null;
        API.error = String((err && err.message) || err);
        if (DEBUG) console.warn('[mos-quote-only] list unavailable, page left untouched:', err);
      });
  }

  /* ---------- handles ---------- */

  function handleFromHref(href) {
    if (!href) return null;
    var m = /\/products\/([^/?#]+)/.exec(href);
    if (!m) return null;
    try { return decodeURIComponent(m[1]).toLowerCase(); } catch (e) { return m[1].toLowerCase(); }
  }

  function isProductLink(node) {
    return !!(node && node.matches && node.matches('a[href*="/products/"]'));
  }

  // Distinct product handles among a node's DESCENDANTS.
  function descendantHandles(node) {
    var seen = [];
    var links = node.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var h = handleFromHref(links[i].getAttribute('href'));
      if (h && seen.indexOf(h) === -1) {
        seen.push(h);
        if (seen.length > 1) break;      // more than one is all we need to know
      }
    }
    return seen;
  }

  // Walk up from the trigger to the smallest scope that describes exactly one product.
  function resolveCard(trigger) {
    var node = trigger.parentElement;
    for (var depth = 0; depth < MAX_DEPTH && node && node !== document.body; depth++) {
      if (isProductLink(node)) {
        var own = handleFromHref(node.getAttribute('href'));
        if (own) return { card: node, handle: own, insideLink: true };
      }
      var found = descendantHandles(node);
      if (found.length === 1) return { card: node, handle: found[0], insideLink: false };
      if (found.length > 1) return null;   // overshot the card — refuse rather than guess
      node = node.parentElement;
    }
    return null;
  }

  /* ---------- triggers ---------- */

  var CART_TEXT = /^\s*(add to cart|add to bag|adding…|adding\.\.\.|added!)\s*$/i;

  function isCartTrigger(el) {
    if (el.hasAttribute(DONE)) return false;
    if (el.getAttribute('name') === 'add') return true;
    if (el.classList && el.classList.contains('quick-add__submit')) return true;
    if (CART_TEXT.test(el.textContent || '')) return true;
    var aria = el.getAttribute('aria-label') || '';
    return /add to cart/i.test(aria) && el.children.length <= 2;
  }

  function cartTriggers(scope) {
    var out = [];
    var candidates = scope.querySelectorAll(
      'button, a[role="button"], input[type="submit"], [name="add"], .quick-add__submit'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (isCartTrigger(candidates[i])) out.push(candidates[i]);
    }
    return out;
  }

  function alreadyHasQuote(card) {
    var els = card.querySelectorAll('button, a, span');
    for (var i = 0; i < els.length; i++) {
      if (els[i].hasAttribute(DONE)) return true;
      if (/^\s*add to quote\s*$/i.test(els[i].textContent || '')) return true;
    }
    return false;
  }

  /* ---------- the change ---------- */

  function convert(trigger, found) {
    // The card root may itself be the <a>. Nesting an <a> inside an <a> is invalid, so use a
    // span there and let the click bubble to the card link.
    var inLink = found.insideLink ||
      !!(trigger.closest && trigger.closest('a[href*="/products/"]'));

    var el;
    if (inLink) {
      el = document.createElement('span');
      el.setAttribute('role', 'link');
      el.style.cursor = 'pointer';
    } else {
      el = document.createElement('a');
      el.setAttribute('href', '/products/' + found.handle);
    }
    el.className = trigger.className;
    // The vendor's button class carries width:100% and symmetric padding but no text
    // alignment, because a <button> centres its own label. A <span> does not, and an
    // inline <span> ignores width:100% entirely, so the label sat left of centre.
    // Inline styles win over the class here (the class has no !important).
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.textAlign = 'center';
    el.style.boxSizing = 'border-box';
    el.setAttribute(DONE, '1');
    el.setAttribute('aria-label', LABEL);
    el.textContent = LABEL;

    if (trigger.parentNode) trigger.parentNode.replaceChild(el, trigger);
    API.converted++;
  }

  function scan() {
    if (!handles || !handles.size) return;
    var triggers = cartTriggers(document);
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      var found = resolveCard(trigger);
      if (!found) { API.refused++; continue; }          // ambiguous or not in a card
      if (!handles.has(found.handle)) continue;         // cartable: leave completely alone
      API.matched++;
      found.card.setAttribute(MARK, '1');
      if (alreadyHasQuote(found.card)) {
        if (trigger.parentNode) trigger.parentNode.removeChild(trigger);
        API.converted++;
      } else {
        convert(trigger, found);
      }
    }
  }

  /* ---------- keeping up with the Preact app ---------- */

  var queued = false;

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      try { scan(); } catch (e) { if (DEBUG) console.warn('[mos-quote-only]', e); }
    });
  }

  function start() {
    loadList().then(function () {
      scan();
      if (window.MutationObserver) {
        new MutationObserver(function (records) {
          for (var i = 0; i < records.length; i++) {
            if (records[i].addedNodes && records[i].addedNodes.length) { schedule(); return; }
          }
        }).observe(document.body, { childList: true, subtree: true });
      }
      [400, 1200, 3000].forEach(function (ms) { setTimeout(schedule, ms); });
      if (DEBUG) readout();
    });
  }

  /* ---------- debug readout ---------- */

  function readout() {
    setTimeout(function () {
      var cards = document.querySelectorAll('a[href*="/products/"]').length;
      var el = document.createElement('pre');
      el.style.cssText =
        'position:fixed;z-index:2147483647;top:8px;left:8px;margin:0;padding:12px 14px;' +
        'background:#0A3370;color:#FFFFFF;font:12px/1.5 ui-monospace,Menlo,monospace;' +
        'border-radius:10px;white-space:pre;user-select:all;max-width:96vw;' +
        'box-shadow:0 6px 24px rgba(0,0,0,.35)';
      el.textContent = [
        'QUOTE-ONLY CARDS  v' + API.version,
        'handles loaded   ' + API.handles + (API.error ? '   ERROR: ' + API.error : ''),
        'product links    ' + cards,
        'cards matched    ' + API.matched + '   (quote-only)',
        'buttons swapped  ' + API.converted,
        'refused          ' + API.refused + '   (ambiguous scope, left alone)',
        '',
        !API.handles
          ? 'LIST NOT LOADED — page deliberately unchanged'
          : (API.matched
              ? 'WORKING — only quote-only cards changed'
              : 'no quote-only products in these results')
      ].join('\n');
      document.body.appendChild(el);
    }, 3500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
